// =============================================================================
// src/features/briefing/briefing-map.ts — Mini-mapa Leaflet do Briefing.
//
// Proveniencia: criado na reforma institucional 2026-06 (D2). Reusa o fetch do
//   GeoJSON e o munPathStyle da Explorar com um estado CONGELADO
//   {colorBy:'partido', year:'2024'} — o mapa abre PINTADO, sem nenhum clique.
// Decisao de produto: SEM tile layer (visual flat Observatory, zero dependencia
//   de rede de tiles) e SEM interacao (pan/zoom off) — o card inteiro e um
//   botao que abre a Explorar. Mesmo padrao ensure()/duplo-rAF da ExploreMap.
// =============================================================================

import L, { type Map as LeafletMap } from 'leaflet';
import type { MunicipalityId } from '../../app/types';
import { GEO_MUN_LITE_URL } from '../explore/constants';
import { munPathStyle, type ExploreStyleState } from '../explore/explore-styling';
import { BRIEFING_ELECTION_YEAR } from './briefing-data';

/** Estado de estilo congelado do mini-mapa: choropleth de partido do ano. */
const BRIEFING_MAP_STYLE_STATE: ExploreStyleState = {
  colorBy: 'partido',
  year: BRIEFING_ELECTION_YEAR,
  party: null,
  region: null,
  micro: null,
  eleitoradoBand: null,
  runnerUp: null,
  // Thresholds nao usados por colorBy='partido'; valores default da ExploreMap.
  densThresholds: [10, 40, 90],
  popThresholds: [15, 30, 60],
};

/** Feature de municipio (mesmo shape do GeoJSON da Explorar). */
type BriefingMunFeature = GeoJSON.Feature<GeoJSON.Geometry, { id: MunicipalityId }>;

/** Controlador do mini-mapa estatico do Briefing. Singleton exportado ao final. */
class BriefingMiniMap {
  ready = false;
  private map: LeafletMap | null = null;

  /** Cria o mapa na 1a abertura; nas seguintes apenas re-mede (padrao ExploreMap). */
  ensure(): void {
    if (this.ready) {
      /* v8 ignore next -- ready=true implica map criado (so vira true apos createMap) */
      this.map?.invalidateSize();
      return;
    }
    if (!document.getElementById('briefingMap')) return;
    this.ready = true;
    void this.paintMunicipalities(this.createMap());
  }

  /** Instancia o L.map SEM interacoes e SEM tiles (flat Observatory). */
  private createMap(): LeafletMap {
    this.map = L.map('briefingMap', {
      center: [-4.6, -64.5], zoom: 5,
      zoomControl: false, attributionControl: false,
      dragging: false, scrollWheelZoom: false, doubleClickZoom: false,
      boxZoom: false, keyboard: false, touchZoom: false,
    });
    return this.map;
  }

  /** Busca o GeoJSON dos municipios, pinta por partido e enquadra o estado. */
  private async paintMunicipalities(map: LeafletMap): Promise<void> {
    const munGeo = await fetch(GEO_MUN_LITE_URL).then((r) => r.json());
    const layer = L.geoJSON(munGeo as GeoJSON.GeoJsonObject, {
      interactive: false,
      style: (feature) => briefingMunStyle(feature as BriefingMunFeature | undefined),
    }).addTo(map);
    map.fitBounds(layer.getBounds(), { padding: [12, 12] });
  }
}

/** Estilo de um municipio no estado congelado (fallback vazio sem feature). */
function briefingMunStyle(feature: BriefingMunFeature | undefined): L.PathOptions {
  if (!feature) return {};
  return munPathStyle(feature.properties.id, BRIEFING_MAP_STYLE_STATE);
}

/** Singleton do mini-mapa do Briefing (mesma forma de uso da ExploreMap). */
export const BriefingMap = new BriefingMiniMap();
